import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  getDocs,
  deleteDoc,
  serverTimestamp,
  DocumentData
} from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { User as FirebaseUser } from 'firebase/auth';

import { 
  UserRole, 
  AdminPermission, 
  usersCollection, 
  adminCollection,
  type User as SchemaUser,
  type UserPreferences
} from '../schema';
import type { AdminUser } from '../schema';

export interface AdminAuthResult {
  user: AdminUser;
  token: string;
}

export interface AdminLoginCredentials {
  email: string;
  password: string;
  adminCode?: string;
}

// AdminUserData is an alias for AdminUser to maintain backward compatibility
type AdminUserData = AdminUser;

interface FirestoreTimestamp {
  toDate: () => Date;
  toMillis: () => number;
  isEqual: (other: FirestoreTimestamp) => boolean;
  valueOf: () => string;
}

// FirestoreAdminUser type is not used and can be removed

import { authService } from './authService';
import { auth } from '../config';

// AdminUserData is defined above as an alias for AdminUser to maintain backward compatibility

export interface AdminAuthResult {
  user: AdminUser;
  token: string;
}

export interface AdminLoginCredentials {
  email: string;
  password: string;
  adminCode?: string; // Optional admin verification code
}

export class AdminService {
  private functions = getFunctions();
  
  // Helper function to safely convert Firestore timestamps to Date
  private toSafeDate(value: unknown): Date | undefined {
    if (value instanceof Date) return value;
    if (value && typeof value === 'object' && 'toDate' in value) {
      return (value as { toDate: () => Date }).toDate();
    }
    return undefined;
  }

  /**
   * Check if a user is an admin
   */
  async isUserAdmin(userId: string): Promise<boolean> {
    try {
      const adminDoc = await getDoc(doc(adminCollection, userId));
      return adminDoc.exists();
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  }

  /**
   * Get admin user data
   */
  async getAdminUser(userId: string): Promise<AdminUser | null> {
    try {
      const adminDoc = await getDoc(doc(adminCollection, userId));
      if (!adminDoc.exists()) {
        return null;
      }
      
      const data = adminDoc.data() as DocumentData;
      
      // Convert Firestore timestamps to Date objects
      const convertTimestamp = (timestamp: unknown): Date | undefined => {
        if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
          return (timestamp as { toDate: () => Date }).toDate();
        }
        return timestamp instanceof Date ? timestamp : undefined;
      };

      // Ensure role is valid, default to ADMIN if not
      const role = (data.role === UserRole.SUPER_ADMIN || data.role === 'super_admin')
        ? UserRole.SUPER_ADMIN
        : UserRole.ADMIN;
      
      // Get user data to merge with admin data
      let userEmail: string | undefined;
      let userDisplayName: string | undefined;
      let userPhotoURL: string | undefined;
      
      try {
        const userDoc = await getDoc(doc(usersCollection, userId));
        if (userDoc.exists()) {
          const userData = userDoc.data() as Partial<SchemaUser> | undefined;
          userEmail = typeof userData?.email === 'string' ? userData.email : undefined;
          userDisplayName = typeof userData?.displayName === 'string' ? userData.displayName : undefined;
          userPhotoURL = typeof userData?.photoURL === 'string' ? userData.photoURL : undefined;
        }
      } catch (error) {
        console.warn('Error fetching user data:', error);
      }
      
      // Build admin user object with all required properties
      const adminUser: AdminUser = {
        id: adminDoc.id,
        userId: typeof data.userId === 'string' ? data.userId : userId,
        role,
        permissions: Array.isArray(data.permissions) 
          ? data.permissions.filter(p => Object.values(AdminPermission).includes(p as AdminPermission)) 
          : [],
        email: typeof data.email === 'string' ? data.email : userEmail,
        displayName: typeof data.displayName === 'string' ? data.displayName : userDisplayName,
        photoURL: typeof data.photoURL === 'string' ? data.photoURL : userPhotoURL,
        createdAt: convertTimestamp(data.createdAt) || new Date(),
        updatedAt: convertTimestamp(data.updatedAt) || new Date(),
        lastAdminAction: convertTimestamp(data.lastAdminAction),
        lastActive: convertTimestamp(data.lastActive) || new Date(),
        preferences: typeof data.preferences === 'object' && data.preferences !== null
          ? {
              preferredResolution: typeof data.preferences.preferredResolution === 'string' 
                ? data.preferences.preferredResolution 
                : '1920x1080',
              theme: data.preferences.theme === 'dark' ? 'dark' as const : 'light' as const,
              adConsent: data.preferences.adConsent === true
            }
          : {
              preferredResolution: '1920x1080',
              theme: 'light',
              adConsent: false
            },
        // Include any additional fields from the document
        ...Object.fromEntries(
          Object.entries(data).filter(([key]) => ![
            'userId', 'role', 'permissions', 'email', 'displayName', 'photoURL',
            'createdAt', 'updatedAt', 'lastAdminAction', 'promotedAt',
            'promotedBy', 'system_initialized', 'preferences', 'lastActive'
          ].includes(key))
        )
      };
      
      return adminUser;
    } catch (error) {
      console.error('Error getting admin user:', error);
      return null;
    }
  }

  /**
   * Create a new admin user
   */
  async createAdminUser(adminData: Omit<AdminUser, 'createdAt' | 'updatedAt' | 'userId'> & { userId?: string }): Promise<AdminUserData> {
    try {
      const timestamp = serverTimestamp();
      const adminRef = doc(adminCollection);
      const userId = adminData.userId || adminRef.id;
      
      // Prepare the admin user data for Firestore
      const adminDataForFirestore: Record<string, unknown> = {
        userId,
        role: typeof adminData.role === 'string' ? adminData.role : 'ADMIN',
        permissions: Array.isArray(adminData.permissions) ? adminData.permissions : [],
        email: typeof adminData.email === 'string' ? adminData.email : undefined,
        displayName: typeof adminData.displayName === 'string' ? adminData.displayName : undefined,
        photoURL: typeof adminData.photoURL === 'string' ? adminData.photoURL : undefined,
        createdAt: timestamp,
        updatedAt: timestamp,
// Include promotedBy if available
        ...(adminData.promotedBy ? { promotedBy: adminData.promotedBy } : {}),
        // Include system_initialized if available
        ...(adminData.system_initialized !== undefined ? { system_initialized: adminData.system_initialized } : {})
      };
      
      // Include any additional fields from adminData
      Object.entries(adminData).forEach(([key, value]) => {
        if (!['id', 'createdAt', 'updatedAt', 'userId', 'role', 'permissions', 'email', 'displayName', 'photoURL', 'promotedBy', 'system_initialized'].includes(key) && value !== undefined) {
          adminDataForFirestore[key] = value;
        }
      });
      
      await setDoc(adminRef, adminDataForFirestore);
      
      // Return the created admin user with proper typing
      return {
        ...adminDataForFirestore,
        id: adminRef.id,
        userId,
        createdAt: new Date(),
        updatedAt: new Date(),
        // Convert timestamps to Date objects for the return value
        ...(adminDataForFirestore.promotedAt ? { promotedAt: new Date() } : {}),
        ...(adminDataForFirestore.lastAdminAction ? { lastAdminAction: new Date() } : {})
      } as AdminUserData;
    } catch (error) {
      console.error('Error creating admin user:', error);
      throw error;
    }
  }

  /**
   * Authenticate admin user with enhanced security
   */
  async authenticateAdminUser(credentials: { email: string; password: string; }): Promise<AdminAuthResult> {
    try {
      // First authenticate with regular auth
      const authUser = await authService.signInWithEmail(
        credentials.email, 
        credentials.password
      );

      // Verify admin status
      const adminUser = await this.verifyAdminUser(authUser.uid);
      if (!adminUser) {
        throw new Error('Access denied: Admin privileges required');
      }

      // Update last admin action
      await this.updateLastAdminAction(adminUser.id);

      // Get custom token with admin claims
      const customToken = await this.getAdminToken(adminUser.id);

      return {
        user: adminUser,
        token: customToken
      };
    } catch (error) {
      console.error('Admin login error:', error);
      throw error;
    }
  }

  /**
   * Verify if user has admin privileges
   */
  async verifyAdminUser(userId: string): Promise<AdminUser | null> {
    try {
      const adminUser = await this.getAdminUser(userId);
      if (!adminUser) {
        return null;
      }
      
      // Try to get user data if available
      let userEmail: string | undefined;
      let userDisplayName: string | undefined;
      let userPhotoURL: string | undefined;
      
      try {
        const userDoc = await getDoc(doc(usersCollection, userId));
        if (userDoc.exists()) {
          const userData = userDoc.data() as Partial<SchemaUser> | undefined;
          userEmail = typeof userData?.email === 'string' ? userData.email : undefined;
          userDisplayName = typeof userData?.displayName === 'string' ? userData.displayName : undefined;
          userPhotoURL = typeof userData?.photoURL === 'string' ? userData.photoURL : undefined;
        } else {
          console.warn('User document not found, using admin data only');
        }
      } catch (error) {
        console.error('Error fetching user document:', error);
      }
      
      // Create verified admin user with merged data
      const verifiedAdmin: AdminUser = {
        ...adminUser,
        email: adminUser.email || userEmail,
        displayName: adminUser.displayName || userDisplayName || '',
        photoURL: adminUser.photoURL || userPhotoURL,
        userId: adminUser.userId || userId,
        role: adminUser.role,
        permissions: Array.isArray(adminUser.permissions) 
          ? adminUser.permissions.filter(p => Object.values(AdminPermission).includes(p as AdminPermission))
          : [],
        lastActive: adminUser.lastActive || new Date(),
        preferences: adminUser.preferences || {
          preferredResolution: '1920x1080',
          theme: 'light',
          adConsent: false
        }
      };
      
      return verifiedAdmin;
    } catch (error) {
      console.error('Error verifying admin user:', error);
      return null;
    }
  }

  /**
   * Check if user has specific admin permission
   */
  async hasPermission(userId: string, permission: AdminPermission): Promise<boolean> {
    try {
      const adminUser = await this.verifyAdminUser(userId);
      if (!adminUser) return false;

      // Super admins have all permissions
      if (adminUser.role === UserRole.SUPER_ADMIN) {
        return true;
      }

      // Ensure permissions is an array before calling includes
      const permissions = Array.isArray(adminUser.permissions) ? adminUser.permissions : [];
      return permissions.includes(permission);
    } catch (error) {
      console.error('Error checking permission:', error);
      return false;
    }
  }

  /**
   * Promote user to admin role (Free tier - no Cloud Functions)
   */
  async promoteToAdmin(
    userId: string, 
    targetUserId: string, 
    role: UserRole.ADMIN | UserRole.SUPER_ADMIN,
    permissions: AdminPermission[]
  ): Promise<void> {
    try {
      // Verify current user has permission to manage admins
      const hasPermission = await this.hasPermission(userId, AdminPermission.MANAGE_ADMINS);
      if (!hasPermission) {
        throw new Error('Insufficient permissions to manage admin users');
      }

      // Update user role
      await updateDoc(doc(usersCollection, targetUserId), {
        role: role,
        updatedAt: serverTimestamp()
      });

      // Create/update admin document
      await setDoc(doc(adminCollection, targetUserId), {
        permissions: permissions,
        promotedBy: userId,
        promotedAt: serverTimestamp(),
        lastAdminAction: null
      }, { merge: true });

      console.log(`✅ User ${targetUserId} promoted to ${role} with database permissions`);
      console.warn('⚠️  Custom claims not set - using database-only admin system');

    } catch (error) {
      console.error('Error promoting user to admin:', error);
      throw error;
    }
  }

  /**
   * Revoke admin privileges (Free tier - no Cloud Functions)
   */
  async revokeAdminAccess(userId: string, targetUserId: string): Promise<void> {
    try {
      // Verify current user has permission to manage admins
      const hasPermission = await this.hasPermission(userId, AdminPermission.MANAGE_ADMINS);
      if (!hasPermission) {
        throw new Error('Insufficient permissions to manage admin users');
      }

      // Prevent self-revocation
      if (userId === targetUserId) {
        throw new Error('Cannot revoke your own admin access');
      }

      // Update user role to regular user
      await updateDoc(doc(usersCollection, targetUserId), {
        role: UserRole.USER,
        updatedAt: serverTimestamp()
      });

      // Remove admin document
      await deleteDoc(doc(adminCollection, targetUserId));

      console.log(`✅ Admin access revoked for user ${targetUserId}`);
      console.warn('⚠️  Custom claims not removed - using database-only admin system');

    } catch (error) {
      console.error('Error revoking admin access:', error);
      throw error;
    }
  }

  /**
   * Update admin permissions (Free tier - no Cloud Functions)
   */
  async updateAdminPermissions(
    userId: string, 
    targetUserId: string, 
    permissions: AdminPermission[]
  ): Promise<void> {
    try {
      // Verify current user has permission to manage admins
      const hasPermission = await this.hasPermission(userId, AdminPermission.MANAGE_ADMINS);
      if (!hasPermission) {
        throw new Error('Insufficient permissions to manage admin users');
      }

      // Update admin permissions
      await updateDoc(doc(adminCollection, targetUserId), {
        permissions: permissions,
        updatedBy: userId,
        updatedAt: serverTimestamp()
      });

      console.log(`✅ Admin permissions updated for user ${targetUserId}`);
      console.warn('⚠️  Custom claims not updated - using database-only admin system');

    } catch (error) {
      console.error('Error updating admin permissions:', error);
      throw error;
    }
  }

  /**
   * Get all admin users from admin collection
   */
  async getAllAdmins(): Promise<AdminUser[]> {
    try {
      // First check if current user is admin
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      // Verify current user is an admin
      const currentAdminDoc = await getDoc(doc(adminCollection, currentUser.uid));
      if (!currentAdminDoc.exists()) {
        throw new Error('Current user is not an admin');
      }

      // Query admin collection directly - this is the source of truth
      const querySnapshot = await getDocs(adminCollection);

      // Process admin users in parallel for better performance
      const adminPromises = querySnapshot.docs.map(async (docSnapshot) => {
        try {
          const adminData = docSnapshot.data() as DocumentData;
          const userId = docSnapshot.id;

          // Skip system documents like 'system_initialized'
          if (userId === 'system_initialized') {
            return null;
          }

          // Get user data if available
          let userEmail: string | undefined;
          let userDisplayName: string | undefined;
          let userPhotoURL: string | undefined;
          let lastActive: Date = new Date();
          let preferences: UserPreferences = {
            preferredResolution: '1920x1080',
            adConsent: false,
            theme: 'light'
          };
          
          try {
            const userDoc = await getDoc(doc(usersCollection, userId));
            if (userDoc.exists()) {
              const userData = userDoc.data() as Partial<SchemaUser> | undefined;
              userEmail = typeof userData?.email === 'string' ? userData.email : undefined;
              userDisplayName = typeof userData?.displayName === 'string' ? userData.displayName : undefined;
              userPhotoURL = typeof userData?.photoURL === 'string' ? userData.photoURL : undefined;
              
              if (userData?.lastActive) {
                const lastActiveDate = this.toSafeDate(userData.lastActive);
                if (lastActiveDate) lastActive = lastActiveDate;
              }
              
              if (userData?.preferences) {
                const userPrefs = userData.preferences as Partial<UserPreferences>;
                preferences = {
                  preferredResolution: typeof userPrefs.preferredResolution === 'string' 
                    ? userPrefs.preferredResolution 
                    : '1920x1080',
                  theme: userPrefs.theme === 'dark' ? 'dark' : 'light',
                  adConsent: userPrefs.adConsent === true
                };
              }
            }
          } catch (error) {
            console.warn(`Error fetching user data for admin ${userId}:`, error);
          }

          // Helper function to safely convert Firestore timestamps to Date
          const toSafeDate = (value: unknown): Date | undefined => {
            if (value instanceof Date) return value;
            if (value && typeof value === 'object' && 'toDate' in value) {
              return (value as { toDate: () => Date }).toDate();
            }
            return undefined;
          };

          // Ensure the role is valid, default to ADMIN if not
          const role = (adminData.role === UserRole.SUPER_ADMIN || 
                       adminData.role === 'super_admin' || 
                       adminData.role === 'SUPER_ADMIN')
            ? UserRole.SUPER_ADMIN
            : UserRole.ADMIN;

          // Get admin data with proper types
          const adminPermissions = Array.isArray(adminData.permissions) 
            ? adminData.permissions.filter((p: string) => 
                Object.values(AdminPermission).includes(p as AdminPermission)
              )
            : [];

          const lastAdminAction = toSafeDate(adminData.lastAdminAction);
          const createdAt = toSafeDate(adminData.createdAt) || new Date();
          const updatedAt = toSafeDate(adminData.updatedAt) || new Date();
          const adminLastActive = toSafeDate(adminData.lastActive) || lastActive;

          // Build the admin user object with proper typing
          const adminUser: AdminUser = {
            id: userId,
            userId: typeof adminData.userId === 'string' ? adminData.userId : userId,
            email: typeof adminData.email === 'string' ? adminData.email : userEmail || '',
            displayName: typeof adminData.displayName === 'string' 
              ? adminData.displayName 
              : userDisplayName || '',
            photoURL: typeof adminData.photoURL === 'string' 
              ? adminData.photoURL 
              : userPhotoURL,
            role,
            permissions: adminPermissions as AdminPermission[],
            preferences,
            lastActive: adminLastActive,
            lastAdminAction,
            createdAt,
            updatedAt,
            // Include any additional fields from the document
            ...Object.fromEntries(
              Object.entries(adminData).filter(([key]) => ![
                'userId', 'role', 'permissions', 'email', 'displayName', 'photoURL',
                'createdAt', 'updatedAt', 'lastAdminAction', 'lastActive',
                'preferences', 'system_initialized'
              ].includes(key))
            )
          };

          return adminUser;
        } catch (error) {
          console.error(`Error processing admin user ${docSnapshot.id}:`, error);
          return null;
        }
      });

      // Wait for all admin data to be processed and filter out any failed ones
      const results = await Promise.all(adminPromises);
      return results.filter((admin): admin is AdminUser => admin !== null);
      
    } catch (error) {
      console.error('Error getting admin users:', error);
      // Return empty array instead of throwing to prevent UI crashes
      return [];
    }
  }

  /**
   * Get admin token with custom claims (fallback for no Cloud Functions)
   */
  private async getAdminToken(userId: string): Promise<string> {
    try {
      // For free tier without Cloud Functions, we'll use a simple token approach
      // This is less secure but works without paid features
      console.warn('Using fallback token method - Cloud Functions not available');
      return `admin-token-${userId}-${Date.now()}`;
    } catch (error) {
      console.error('Error getting admin token:', error);
      throw error;
    }
  }

  /**
   * Update last admin action timestamp
   */
  private async updateLastAdminAction(userId: string): Promise<void> {
    try {
      await updateDoc(doc(adminCollection, userId), {
        lastAdminAction: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating last admin action:', error);
      // Don't throw as this is not critical
    }
  }

  /**
   * Get default permissions based on role
   */
  private getDefaultPermissions(role: UserRole): AdminPermission[] {
    switch (role) {
      case UserRole.SUPER_ADMIN:
        return Object.values(AdminPermission);
      case UserRole.ADMIN:
        return [
          AdminPermission.MANAGE_WALLPAPERS,
          AdminPermission.MANAGE_CATEGORIES,
          AdminPermission.VIEW_ANALYTICS
        ];
      default:
        return [];
    }
  }

  /**
   * Validate admin credentials format
   */
  validateAdminCredentials(credentials: AdminLoginCredentials): boolean {
    return !!(
      credentials.email &&
      credentials.password &&
      credentials.email.includes('@') &&
      credentials.password.length >= 8
    );
  }

  /**
   * Check if current user is admin
   */
  async isCurrentUserAdmin(): Promise<boolean> {
    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser) return false;

      const adminUser = await this.verifyAdminUser(currentUser.uid);
      return !!adminUser;
    } catch (error) {
      console.error('Error checking current user admin status:', error);
      return false;
    }
  }

  /**
   * Find user by email (admin only)
   */
  async getUserByEmail(email: string): Promise<SchemaUser | null> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      // Verify current user is an admin
      const isAdmin = await this.isCurrentUserAdmin();
      if (!isAdmin) {
        throw new Error('Admin privileges required');
      }

      // Query users collection by email
      const querySnapshot = await getDocs(usersCollection);
      let foundUser: SchemaUser | null = null;

      querySnapshot.forEach((doc) => {
        const data = doc.data() as DocumentData;
        if (data.email && data.email.toLowerCase() === email.toLowerCase()) {
          foundUser = {
            id: doc.id,
            email: data.email || '',
            displayName: data.displayName || '',
            photoURL: data.photoURL || null,
            role: data.role || UserRole.USER,
            preferences: data.preferences || {
              preferredResolution: '1920x1080',
              theme: 'light',
              adConsent: false
            },
            createdAt: this.toSafeDate(data.createdAt) || new Date(),
            lastActive: this.toSafeDate(data.lastActive) || new Date()
          };
        }
      });

      return foundUser;
    } catch (error) {
      console.error('Error finding user by email:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const adminService = new AdminService();

// Export auth state listener with a different name to avoid conflicts
import { onAuthStateChanged as firebaseOnAuthStateChanged } from 'firebase/auth';

export const onAuthStateChanged = (callback: (user: FirebaseUser | null) => void) => {
  return firebaseOnAuthStateChanged(auth, callback);
};